// Carer weekly availability schedule editor.
// Opens as a side drawer; writes PATCH /carers/:id/schedule.

import { useState } from "react";
import type { JSX } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api.js";

type Day = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
interface TimeSlot { start: string; end: string; }
type Schedule = Partial<Record<Day, TimeSlot[]>>;

const DAYS: { key: Day; label: string }[] = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const DEFAULT_SLOT: TimeSlot = { start: "09:00", end: "17:00" };

interface Props {
  carerId: string;
  carerName: string;
  initialSchedule: Schedule;
  onClose: () => void;
}

interface DayRowProps {
  dayKey: Day; label: string; slots: TimeSlot[] | undefined;
  onToggle: () => void;
  onUpdate: (idx: number, field: keyof TimeSlot, value: string) => void;
  onAdd: () => void;
  onRemove: (idx: number) => void;
}

function DayRow({ dayKey: _dayKey, label, slots, onToggle, onUpdate, onAdd, onRemove }: DayRowProps): JSX.Element {
  const enabled = !!slots;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <button onClick={onToggle} className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${enabled ? "bg-navy-500" : "bg-slate-300"}`}>
          <span className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`} />
        </button>
      </div>
      {enabled && (
        <div className="pl-3 space-y-2">
          {slots.map((slot, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input type="time" value={slot.start} onChange={(e) => onUpdate(idx, "start", e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-xs w-24" />
              <span className="text-xs text-slate-400">to</span>
              <input type="time" value={slot.end} onChange={(e) => onUpdate(idx, "end", e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-xs w-24" />
              <button onClick={() => onRemove(idx)} className="text-slate-400 hover:text-red-500 text-lg leading-none">&times;</button>
            </div>
          ))}
          <button onClick={onAdd} className="text-xs text-navy-700 hover:text-navy-800">+ Add slot</button>
        </div>
      )}
    </div>
  );
}

export function CarerScheduleDrawer({ carerId, carerName, initialSchedule, onClose }: Props): JSX.Element {
  const qc = useQueryClient();
  const [schedule, setSchedule] = useState<Schedule>({ ...initialSchedule });

  const save = useMutation({
    mutationFn: () => apiFetch(`/carers/${carerId}/schedule`, { method: "PATCH", body: JSON.stringify(schedule) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["carers"] }); onClose(); },
  });

  function toggleDay(day: Day): void {
    setSchedule((s) => { const next = { ...s }; if (next[day]) { delete next[day]; } else { next[day] = [{ ...DEFAULT_SLOT }]; } return next; });
  }

  function updateSlot(day: Day, idx: number, field: keyof TimeSlot, value: string): void {
    setSchedule((s) => { const slots = [...(s[day] ?? [])]; slots[idx] = { ...(slots[idx] ?? DEFAULT_SLOT), [field]: value } as TimeSlot; return { ...s, [day]: slots }; });
  }

  function addSlot(day: Day): void {
    setSchedule((s) => ({ ...s, [day]: [...(s[day] ?? []), { ...DEFAULT_SLOT }] }));
  }

  function removeSlot(day: Day, idx: number): void {
    setSchedule((s) => { const slots = (s[day] ?? []).filter((_, i) => i !== idx); if (slots.length === 0) { const next = { ...s }; delete next[day]; return next; } return { ...s, [day]: slots }; });
  }

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <aside className="w-96 bg-white shadow-xl flex flex-col overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Weekly Schedule</h2>
            <p className="text-xs text-slate-500">{carerName}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>
        <div className="flex-1 px-6 py-4 space-y-4">
          {DAYS.map(({ key, label }) => (
            <DayRow key={key} dayKey={key} label={label} slots={schedule[key]}
              onToggle={() => toggleDay(key)}
              onUpdate={(idx, field, val) => updateSlot(key, idx, field, val)}
              onAdd={() => addSlot(key)}
              onRemove={(idx) => removeSlot(key, idx)}
            />
          ))}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 shrink-0">
          {save.isError && <p className="text-xs text-red-600 self-center">Failed to save.</p>}
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={() => save.mutate()} disabled={save.isPending} className="px-4 py-2 bg-navy-700 text-white text-sm rounded-lg hover:bg-navy-800 disabled:opacity-50">
            {save.isPending ? "Saving…" : "Save Schedule"}
          </button>
        </div>
      </aside>
    </div>
  );
}
